import { useTranslation } from 'react-i18next';
import { z } from 'zod/v3';

import type { TextInputProps } from '@highlight-cp/core-ui';
import { TextInput } from '@highlight-cp/core-ui';

import ExternalLinkIcon from '../../action-icons/external-link-icon';

const DEFAULT_DEAL_RECORD_BASE_URL = 'https://app.hubspot.com/contacts/7306769/record/0-3/';

/**
 * A component that renders a text input for a Hubspot Deal ID.
 * @param rest - The rest of the props to pass to the text input.
 * @returns A text input for a Hubspot Deal ID.
 */
const HubspotDealIdInput = ({
  value,
  recordUrlBase = DEFAULT_DEAL_RECORD_BASE_URL,
  ...rest
}: TextInputProps & {
  /** Base URL a deal id is appended to, for the HubSpot portal in use. Defaults to production. */
  recordUrlBase?: string;
}) => {
  const { t } = useTranslation();

  // Generate HubSpot URL if we have a valid deal ID
  const generateHubspotUrl = (dealId: string | null | undefined): string => {
    return `${recordUrlBase}${dealId?.trim()}`;
  };

  // Check if the value is a valid deal ID (10-12 digits)
  const isValidDealId = value && /^\d{10,12}$/.test(value.toString());

  // Create the label with optional HubSpot link
  const labelElement = isValidDealId ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <ExternalLinkIcon
        href={generateHubspotUrl(value?.toString())}
        title={t('Open HubSpot deal in new tab')}
        size={14}
      />
      {t('Hubspot Deal ID')}
    </span>
  ) : (
    t('Hubspot Deal ID')
  );

  return (
    <TextInput
      description={t('Use the parent deal ID. If none, use 00000000000.')}
      label={labelElement}
      placeholder="38765227929"
      required
      maxLength={12}
      value={value}
      {...rest}
    />
  );
};

/**
 * A zod validator for a Hubspot Deal ID.
 * @usage
 * ```ts
 * const form = useForm({
 *   validate: schemaResolver(
 *     z.object({
 *       hubspotDealId: HubspotDealIdInput.zodValidator,
 *     }),
 *     { sync: true },
 *   ),
 * });
 * ```
 * @returns A zod validator for a Hubspot Deal ID.
 */
HubspotDealIdInput.zodValidator = z
  .string()
  .regex(/^\d{10,12}$/, 'Hubspot Deal ID must be 10-12 digits');

export default HubspotDealIdInput;
